import React, { useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { containerVariants, cardVariants, fadeUp } from '../utils/variants';
import { COLORS } from '../constants/colors';
import { motion } from 'framer-motion';

const ERPModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    city: '',
    message: '',
    demoType: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const WHATSAPP_NUMBER = '919254147455'; 
  
  // Initialize AOS animations
  AOS.init();

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Prepare the message text to send via WhatsApp
    const message = `
      *Demo Request Form Details:*\n
      *Name*: ${form.name}\n
      *Email*: ${form.email}\n
      *Phone*: ${form.phone}\n
      *School*: ${form.school}\n
      *City*: ${form.city}\n
      *Message*: ${form.message}\n
      *Demo Type*: ${form.demoType}
    `;
    
    // URL encode the message for WhatsApp
    const encodedMessage = encodeURIComponent(message);

    // Create the WhatsApp API URL with the encoded message
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    try {
      // Open WhatsApp chat with the prefilled message
      window.open(whatsappUrl, '_blank');
      setSuccess('Message sent successfully!');
      setForm({ name: '', email: '', phone: '', message: '', demoType: '', school: '', city: '' });
      setError('');
    } catch (err) {
      setError('Failed to send message. Please try again.');
      setSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {/* Floating Message Icon */}
      <div
        className="fixed right-6 bottom-6 cursor-pointer bg-[#0284C7] text-white z-50 p-5 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        onClick={toggleModal}
      >
        <i className="fa-solid fa-envelope fa-2xl"></i>
      </div>

      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50 transition-opacity duration-300">
          <motion.div
            className="bg-gradient-to-br from-[#E0F2FE] to-[#FFFFFF] rounded-lg shadow-lg md:p-6 p-4 max-w-lg w-full transition-transform duration-300"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-2xl font-semibold text-[#1E293B]">Request a Demo Credentials</h2>
              <button onClick={toggleModal} className="text-[#1E293B] text-3xl hover:text-[#475569]">
                &times;
              </button>
            </div>

            <p className="text-[#1E293B] md:text-left mb-3">
              Select a demo and fill out the form to schedule your session!
            </p>

            {/* Form */}
            <motion.div
              className="bg-white bg-opacity-70 p-4 md:p-5 rounded-lg shadow-inner"
              variants={cardVariants}
            >
              <form onSubmit={handleSubmit}>
                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="school"
                    value={form.school}
                    onChange={handleChange}
                    placeholder="Enter School Name"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>
                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter Name"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>
                <label className="block mb-3">
                  <motion.input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter Email"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>
                <label className="block mb-3">
                  <motion.input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>
                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Enter School City"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                {/* Demo Type Selector */}
                <label className="block mb-3">
                  <motion.select
                    name="demoType"
                    value={form.demoType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  >
                    <option value="">Select Demo Type</option>
                    <option value="admin">Admin Demo</option>
                    <option value="student">Student Demo</option>
                    <option value="teacher">Teacher Demo</option>
                    <option value="library">Library Demo</option>
                  </motion.select>
                </label>

                <label className="block mb-3">
                  <motion.textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Enter Message"
                    className="w-full px-3 py-2 rounded-2xl border border-transparent bg-[#FFFFFF] bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    rows="3"
                    required
                    variants={fadeUp}
                  ></motion.textarea>
                </label>

                {success && <p className="text-green-500 text-center">{success}</p>}
                {error && <p className="text-red-500 text-center">{error}</p>}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0284C7] text-[#FFFFFF] py-3 rounded-full hover:bg-[#0369A1] transition duration-200"
                  variants={fadeUp}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ERPModal;
 